import { useTranslation } from 'react-i18next'
import {
  convert,
  envelopeRange,
  fplContactsFor,
  formatHhMm,
  sunTimes,
  type EnvelopePoint,
  type FlightPlanFigures,
  type LoadingStation,
  type PlanAircraftFigures,
  type PlanLegInput,
  type RouteFrequencyRow,
  type SunEvents,
} from '@jupi/shared'
import { formatDate, formatDateTime, formatTime } from '@jupi/ui'
import { pad3 } from './PlanPage'
import { PILOT_LOG_CSS } from './pilotLogCss'

// The one-page pilot log (#176): A4 landscape, folded down the middle into
// two A5 pages. The left page is the one on the kneeboard in flight (times,
// navigation log, who to call); the right is the planning reference (speeds,
// fuel, weight and balance). Drawn from the cached plan, so it prints with no
// signal. Plain elements and one scoped stylesheet rather than MUI: a printed
// sheet is a fixed layout in millimetres of paper, not a responsive page.

const NAV_ROWS = 8
// A long route fills the log with names and frequencies of its own, so the
// page gives up its extras first: leg names, then airspace rows. The lines a
// pilot cannot do without (frequencies to call, the FPL contacts) stay.
const DENSE_FROM_LEGS = 6
const MAX_AIRSPACE_ROWS = 5
const MAX_AIRSPACE_ROWS_DENSE = 2
const MAX_AREAS = 8
const EMERGENCY_MHZ = '121.500'

const STATION_LABEL: Record<LoadingStation, string> = {
  empty: 'performance.emptyWeight',
  front: 'performance.armFrontPax',
  rear: 'performance.armRearPax',
  cargo: 'performance.armCargo',
  fuelMain: 'performance.armFuelMain',
  fuelRes: 'performance.armFuelRes',
}

const SPEEDS = [
  'stallSpeedFlaps',
  'stallSpeedClean',
  'approachSpeed',
  'bestGlideSpeed',
  'bestClimbSpeed',
  'steepestClimbSpeed',
] as const

export interface PilotLogSheetProps {
  legs: PlanLegInput[]
  figures: FlightPlanFigures
  aircraft: PlanAircraftFigures | null
  registration: string | null
  windDir: number
  windSpeedKt: number
  reserveMinutes: number
  pilotName: string
  persons: number
  /** Today, YYYY-MM-DD in the club's time zone. */
  today: string
  /** When the plan was last saved: which copy this is. */
  savedAt: string | null
  /** Controlled and restricted airspace on the route; null while the airspace data is unavailable. */
  airspace: RouteFrequencyRow[] | null
  /** FIR and flight information zone frequencies along the route. */
  enRoute: RouteFrequencyRow[]
}

const blank = (n: number) => Array.from({ length: Math.max(0, n) }, (_, i) => i)

export function PilotLogSheet(props: PilotLogSheetProps) {
  const { t } = useTranslation()
  const { legs, figures, aircraft, airspace } = props
  const first = legs[0]
  const last = legs[legs.length - 1]
  const { fuel, weightBalance: wb } = figures
  const performance = aircraft?.performance ?? null
  const contacts = fplContactsFor([first?.code, last?.code])
  const ifr = props.reserveMinutes > 30

  // The aerodromes' own frequencies; the leg's COM is already in the log.
  const aerodromeRows = legs
    .map((leg) => ({
      code: leg.code,
      items: [
        [t('plan.sheet.gnd'), leg.gnd],
        [t('plan.sheet.atis'), leg.atis],
        [t('plan.sheet.area'), leg.area],
        [t('plan.sheet.vor'), leg.vor],
      ].filter((item): item is [string, string] => !!item[1]),
    }))
    .filter((row) => row.items.length > 0)

  // Units to call first; prohibited, restricted and danger areas have no
  // frequency, so they are one line of designators, not rows.
  const calls = airspace?.filter((row) => row.freq) ?? []
  const areas = airspace?.filter((row) => !row.freq) ?? []
  const dense = legs.length >= DENSE_FROM_LEGS
  const shownAirspace = calls.slice(0, dense ? MAX_AIRSPACE_ROWS_DENSE : MAX_AIRSPACE_ROWS)
  const hiddenAirspace = calls.length - shownAirspace.length
  const speeds = SPEEDS.map((key) => [key, performance?.[key] ?? null] as const).filter(([, value]) => value)

  return (
    <article className="pilot-log" aria-label={t('plan.sheet.title')}>
      <style>{PILOT_LOG_CSS}</style>
      <div className="fold" />

      <section className="page left">
        <header className="hd">
          <div className="row1">
            <div className="route">
              {first?.code} → {last?.code}
            </div>
            <div className="reg">{props.registration ?? ''}</div>
          </div>
          <div className="meta">
            {first?.name && last?.name ? (
              <span>
                {first.name} – {last.name}
              </span>
            ) : null}
            {aircraft?.aircraftType ? <span>{aircraft.aircraftType}</span> : null}
            <span>
              {t('plan.sheet.date')} <b>{formatDate(props.today)}</b>
            </span>
          </div>
          <div className="meta">
            <span>
              {t('plan.sheet.pic')} <b>{props.pilotName}</b>
            </span>
            <span>
              {t('plan.sheet.pob')} <b>{props.persons}</b>
            </span>
            <span>
              {t('plan.editor.wind')}{' '}
              <b className="mono">
                {pad3(props.windDir)}/{Math.round(props.windSpeedKt)}
              </b>
            </span>
            <span>
              {t('plan.sheet.rules')} <b>{ifr ? 'IFR' : 'VFR'}</b>
            </span>
          </div>
        </header>

        <section className="times" aria-label={t('plan.sheet.times')}>
          <div>
            <label>{t('plan.sheet.engineStart')}</label>
            <span className="fill" />
          </div>
          <div>
            <label>{t('plan.sheet.offBlock')}</label>
            <span className="fill" />
          </div>
          <div>
            <label>{t('plan.sheet.takeOff')}</label>
            <span className="fill" />
          </div>
          <div className="plan">
            <label>{t('plan.sheet.planned')}</label>
            <span className="fig">
              {formatHhMm(figures.totals.minutes)} <small>{t('plan.editor.ete')}</small>
            </span>
            <span className="line mono">{figures.totals.distanceNm.toFixed(0)} NM</span>
            {figures.cruiseKt !== null ? <span className="line mono">TAS {Math.round(figures.cruiseKt)} kt</span> : null}
            {fuel ? (
              <>
                <span className="line">
                  {t('plan.sheet.fuelNeeded')} <b className="mono">{fuel.requiredUsg.toFixed(1)}</b> USG
                </span>
                <span className="line">
                  {t('plan.weight.endurance')} <b className="mono">{formatHhMm(fuel.enduranceMinutes)}</b>
                </span>
              </>
            ) : null}
          </div>
          <div>
            <label>{t('plan.sheet.landing')}</label>
            <span className="fill" />
          </div>
          <div>
            <label>{t('plan.sheet.onBlock')}</label>
            <span className="fill" />
          </div>
          <div>
            <label>{t('plan.sheet.engineStop')}</label>
            <span className="fill" />
          </div>
        </section>

        <section>
          <h2>{t('plan.ofp.navLog')}</h2>
          <table className={dense ? 'nav dense' : 'nav'}>
            <thead>
              <tr>
                <th>{t('plan.sheet.waypoint')}</th>
                <th>COM</th>
                <th>{t('plan.editor.tc')}</th>
                <th>{t('plan.editor.mh')}</th>
                <th>NM</th>
                <th>{t('plan.editor.gs')}</th>
                <th>{t('plan.editor.ete')}</th>
                <th>ETO</th>
                <th>ATO</th>
              </tr>
            </thead>
            <tbody>
              {legs.map((leg, index) => {
                const f = figures.legs[index]!
                return (
                  <tr key={index}>
                    <td className="wp">
                      {leg.code}
                      {leg.name ? <span className="sub">{leg.name}</span> : null}
                    </td>
                    <td className="com">{leg.com ?? ''}</td>
                    <td className="num">{index > 0 && f.trueCourse !== null ? pad3(f.trueCourse) : ''}</td>
                    <td className={index > 0 ? 'num mh' : 'num'}>{index > 0 && f.magHeading !== null ? pad3(f.magHeading) : ''}</td>
                    <td className="num">{index > 0 && f.distanceNm !== null ? Math.round(f.distanceNm) : ''}</td>
                    <td className="num">{index > 0 && f.groundSpeedKt !== null ? Math.round(f.groundSpeedKt) : ''}</td>
                    <td className="num">
                      <b>{index > 0 && f.minutes !== null ? formatHhMm(f.minutes) : ''}</b>
                    </td>
                    <td className="write" />
                    <td className="write" />
                  </tr>
                )
              })}
              {blank(NAV_ROWS - legs.length).map((i) => (
                <tr key={`blank${i}`}>
                  {blank(9).map((c) => (
                    <td key={c} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        {aerodromeRows.length > 0 ? (
          <section className="box">
            <h2>{t('plan.sheet.aerodromeFrequencies')}</h2>
            <table className="fq">
              <tbody>
                {aerodromeRows.map((row, index) => (
                  <tr key={index}>
                    <td className="n">{row.code}</td>
                    <td>
                      {row.items.map(([label, value]) => (
                        <span key={label} className="item">
                          {label} <b className="mono">{value}</b>
                        </span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        ) : null}

        <section className="box">
          <h2>{t('plan.sheet.airspace')}</h2>
          {airspace === null ? (
            <div className="empty">{t('plan.sheet.airspaceUnavailable')}</div>
          ) : shownAirspace.length === 0 ? (
            <div className="empty">{t('plan.sheet.airspaceNone')}</div>
          ) : (
            <ul className="airspace">
              {shownAirspace.map((row) => (
                <li key={row.id}>
                  <b>{row.id}</b>
                  <span>
                    {row.callsign ?? ''}
                    <span className="rule">
                      {' '}
                      · {row.lower} – {row.upper}
                    </span>
                  </span>
                  <span className="f">{row.freq}</span>
                </li>
              ))}
              {hiddenAirspace > 0 ? <li className="more">{t('plan.sheet.airspaceMore', { count: hiddenAirspace })}</li> : null}
            </ul>
          )}
          {areas.length > 0 ? (
            <div className="enroute areas">
              <span className="tag">{t('plan.sheet.restricted')}</span>
              <span className="mono">
                {areas.slice(0, MAX_AREAS).map((row) => row.id).join(' · ')}
                {areas.length > MAX_AREAS ? ` +${areas.length - MAX_AREAS}` : ''}
              </span>
            </div>
          ) : null}
          <div className="enroute">
            <span className="tag">{t('plan.sheet.enRoute')}</span>
            {props.enRoute.map((row) => (
              <span key={row.id} className="item">
                {row.callsign ?? row.id} <b className="mono">{row.freq}</b>
              </span>
            ))}
            <span className="item">
              {t('plan.sheet.emergency')} <b className="mono">{EMERGENCY_MHZ}</b>
            </span>
          </div>
        </section>

        {contacts.length > 0 ? (
          <div className="contacts">
            <b>{t('plan.sheet.fplContacts')}</b>
            <span>
              {contacts.map((c, index) => (
                <span key={c.unit}>
                  {index > 0 ? ' · ' : ''}
                  {c.unit} <span className="mono">{c.phones.join(` ${t('plan.sheet.or')} `)}</span>
                </span>
              ))}
            </span>
          </div>
        ) : null}

        <div className="pgfoot">
          <span>{t('plan.sheet.inFlight')}</span>
          {props.savedAt ? <span>{t('plan.ofp.savedAt', { time: formatDateTime(props.savedAt) })}</span> : null}
        </div>
      </section>

      <section className="page right">
        <div className="top">
          <div className="tag">
            {t('plan.sheet.reference')} · {props.registration ?? ''}
          </div>
          {wb ? (
            <div className={`verdict${wb.ok ? '' : ' bad'}`}>{wb.ok ? t('plan.editor.wbOk') : t('plan.editor.wbNotOk')}</div>
          ) : null}
        </div>

        <div className="two">
          {speeds.length > 0 ? (
            <div className="box speeds">
              <h2>
                {t('performance.speeds')} <span>{performance?.speedUnit ?? 'kt'}</span>
              </h2>
              <table>
                <tbody>
                  {speeds.map(([key, value]) => (
                    <tr key={key}>
                      <td>{t(`performance.${key}`)}</td>
                      <td className="num">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
          {fuel ? (
            <div className="box">
              <h2>
                {t('plan.sheet.fuel')} <span>USG</span>
              </h2>
              <table>
                <tbody>
                  {aircraft?.fuelConsumptionUsg != null ? (
                    <tr>
                      <td>{t('plan.sheet.burn')}</td>
                      <td className="num">{aircraft.fuelConsumptionUsg.toFixed(1)}</td>
                    </tr>
                  ) : null}
                  <tr>
                    <td>{t('plan.editor.fuelTrip')}</td>
                    <td className="num">{fuel.tripUsg.toFixed(1)}</td>
                  </tr>
                  <tr>
                    <td>{t('plan.weight.contingency')}</td>
                    <td className="num">{fuel.contingencyUsg.toFixed(1)}</td>
                  </tr>
                  <tr>
                    <td>
                      {t('plan.weight.reserve')} {props.reserveMinutes} min
                    </td>
                    <td className="num">{fuel.reserveUsg.toFixed(1)}</td>
                  </tr>
                  <tr className="key">
                    <td>{t('plan.sheet.minimumFuel')}</td>
                    <td className="num">{fuel.requiredUsg.toFixed(1)}</td>
                  </tr>
                  <tr>
                    <td>{t('plan.weight.usable')}</td>
                    <td className="num">{fuel.usableUsg.toFixed(1)}</td>
                  </tr>
                  <tr>
                    <td>{t('plan.weight.onArrival')}</td>
                    <td className="num">{fuel.onArrivalUsg.toFixed(1)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        {wb && performance ? (
          <div className="two">
            <div className="box">
              <h2>
                {t('plan.sheet.loading')} <span>kg</span>
              </h2>
              <table>
                <tbody>
                  {wb.rows
                    .filter((row) => row.station === 'empty' || row.kg > 0)
                    .map((row) => (
                      <tr key={row.station}>
                        <td>{t(STATION_LABEL[row.station])}</td>
                        <td className="num">{Math.round(row.kg)}</td>
                      </tr>
                    ))}
                  <tr className="key">
                    <td>{t('plan.sheet.takeoffWeight')}</td>
                    <td className="num">{Math.round(wb.takeoff.kg)}</td>
                  </tr>
                  {aircraft?.mtowKg ? (
                    <tr>
                      <td>{t('plan.weight.mtow')}</td>
                      <td className="num">{aircraft.mtowKg}</td>
                    </tr>
                  ) : null}
                  <tr>
                    <td>{t('plan.sheet.landingWeight')}</td>
                    <td className="num">{Math.round(wb.landing.kg)}</td>
                  </tr>
                  <tr>
                    <td>{t('plan.sheet.cg')}</td>
                    <td className="num">
                      {wb.takeoff.cgArm?.toFixed(3) ?? '–'} · {wb.landing.cgArm?.toFixed(3) ?? '–'}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="box">
              <h2>{t('performance.envelope')}</h2>
              <Envelope
                envelope={performance.cgEnvelope}
                takeoff={wb.takeoff.cgArm !== null ? { arm: wb.takeoff.cgArm, kg: wb.takeoff.kg } : null}
                landing={wb.landing.cgArm !== null ? { arm: wb.landing.cgArm, kg: wb.landing.kg } : null}
                labels={{ takeoff: t('plan.weight.takeoff'), landing: t('plan.weight.landing'), mass: t('plan.sheet.massKg') }}
              />
            </div>
          </div>
        ) : null}

        <section className="box">
          <h2>{t('plan.sheet.aerodromes')}</h2>
          <table className="ad">
            <tbody>
              {[first, last].map((leg, index) =>
                leg ? (
                  <tr key={index}>
                    <td className="code">{leg.code}</td>
                    <td>{leg.name ?? ''}</td>
                    <td className="num">{sunLine(props.today, leg, t('weather.sun.midnightSun'), t('weather.sun.polarNight'))}</td>
                  </tr>
                ) : null,
              )}
            </tbody>
          </table>
          <div className="src">{t('weather.sun.localTime')}</div>
        </section>

        <div className="notes">
          <h2>{t('plan.sheet.notes')}</h2>
        </div>
        <div className="pgfoot">
          <span>{t('plan.sheet.disclaimer')}</span>
        </div>
      </section>
    </article>
  )
}

function eventsText(events: SunEvents, always: string, never: string) {
  if (events.state === 'alwaysUp') return always
  if (events.state === 'alwaysDown') return never
  return `↑ ${formatTime(events.rise!)}  ↓ ${formatTime(events.set!)}`
}

// Sunrise and sunset at a leg, where it has a position.
function sunLine(today: string, leg: PlanLegInput, always: string, never: string) {
  if (leg.lat === null || leg.lon === null) return ''
  return eventsText(sunTimes(today, leg.lat, leg.lon).sun, always, never)
}

// The CG envelope on paper: the sheet is white whatever the app's theme, so
// it takes no colour from it. Same axes as the app's EnvelopeChart.
export function Envelope({
  envelope,
  takeoff,
  landing,
  labels,
  bothUnits = false,
}: {
  /** Label the axes in kg and lb, m and in. */
  bothUnits?: boolean
  envelope: EnvelopePoint[]
  takeoff: { arm: number; kg: number } | null
  landing: { arm: number; kg: number } | null
  labels: { takeoff: string; landing: string; mass: string }
}) {
  const markers = [takeoff, landing].filter((m): m is { arm: number; kg: number } => m !== null)
  const range = envelopeRange([...envelope, ...markers])
  if (!range || envelope.length < 3) return null

  const W = 240
  const H = bothUnits ? 190 : 170
  const pad = { left: bothUnits ? 54 : 40, right: 12, top: 14, bottom: bothUnits ? 38 : 26 }
  const armSpan = range.maxArm - range.minArm || 0.1
  const kgSpan = range.maxKg - range.minKg || 100
  const minArm = range.minArm - armSpan * 0.08
  const maxArm = range.maxArm + armSpan * 0.08
  const minKg = range.minKg - kgSpan * 0.08
  const maxKg = range.maxKg + kgSpan * 0.08
  const x = (arm: number) => pad.left + ((arm - minArm) / (maxArm - minArm)) * (W - pad.left - pad.right)
  const y = (kg: number) => H - pad.bottom - ((kg - minKg) / (maxKg - minKg)) * (H - pad.top - pad.bottom)
  const armTicks = [range.minArm, (range.minArm + range.maxArm) / 2, range.maxArm]
  const kgTicks = [range.minKg, (range.minKg + range.maxKg) / 2, range.maxKg]

  const diamond = (m: { arm: number; kg: number }, fill: string) => (
    <rect x={-4} y={-4} width={8} height={8} fill={fill} transform={`translate(${x(m.arm)} ${y(m.kg)}) rotate(45)`} />
  )

  return (
    <>
      <div className="legend">
        <span>
          <i style={{ background: '#d22b2b' }} />
          {labels.takeoff}
        </span>
        <span>
          <i style={{ background: '#1f4fd1' }} />
          {labels.landing}
        </span>
      </div>
      <svg className="chart" viewBox={`0 0 ${W} ${H}`} role="img" aria-label={labels.mass}>
        <rect x={pad.left} y={pad.top} width={W - pad.left - pad.right} height={H - pad.top - pad.bottom} fill="#ffffff" stroke="#c5cad1" />
        {armTicks.map((arm) => (
          <g key={`a${arm}`}>
            <line x1={x(arm)} y1={pad.top} x2={x(arm)} y2={H - pad.bottom} stroke="#dfe3e8" />
            <text x={x(arm)} y={H - pad.bottom + 12} textAnchor="middle">
              {arm.toFixed(2)}
              {bothUnits ? (
                <>
                  {' m'}
                  <tspan x={x(arm)} dy={10}>
                    {convert('arm', arm, 'imperial').toFixed(0)} in
                  </tspan>
                </>
              ) : null}
            </text>
          </g>
        ))}
        {kgTicks.map((kg) => (
          <g key={`k${kg}`}>
            <line x1={pad.left} y1={y(kg)} x2={W - pad.right} y2={y(kg)} stroke="#dfe3e8" />
            <text x={pad.left - 4} y={y(kg) + (bothUnits ? -1 : 3)} textAnchor="end">
              {Math.round(kg)}
              {bothUnits ? (
                <>
                  {' kg'}
                  <tspan x={pad.left - 4} dy={9}>
                    {Math.round(convert('mass', kg, 'imperial'))} lb
                  </tspan>
                </>
              ) : null}
            </text>
          </g>
        ))}
        <polygon points={envelope.map((p) => `${x(p.arm)},${y(p.kg)}`).join(' ')} fill="#d6e6e4" stroke="#4d8a86" strokeWidth={1.5} />
        {takeoff ? diamond(takeoff, '#d22b2b') : null}
        {landing ? diamond(landing, '#1f4fd1') : null}
        {bothUnits ? null : (
          <text x={9} y={H / 2} transform={`rotate(-90 9 ${H / 2})`} textAnchor="middle">
            {labels.mass}
          </text>
        )}
      </svg>
    </>
  )
}
